import { readonly, ref } from 'vue'
import { clearStoredAuthTokens, fetchAuthState, getStoredBearerToken, getStoredJwtToken, signInWithEmail, signOutFromAuth, signUpWithEmail, type AuthSessionPayload } from '@/services/authClient'
import { useEventUserRolesStore } from '@/stores/eventUserRoles'
import { pinia } from '@/stores/pinia'
import { useUserStore } from '@/stores/user'

const session = ref<AuthSessionPayload | null>(null)
const loaded = ref(false)
const initialized = ref(false)
let initializationPromise: Promise<void> | null = null

function userStore() {
  return useUserStore(pinia)
}

function rolesStore() {
  return useEventUserRolesStore(pinia)
}

function resetLocalAuthState() {
  session.value = null
  clearStoredAuthTokens()
  userStore().setLogout()
  rolesStore().reset()
}

export function hydrateAuthFromStorage() {
  const token = getStoredJwtToken()
  if (token) {
    userStore().setLogin(token)
  }
}

export async function ensureRolesLoaded() {
  const roles = rolesStore()
  if (!userStore().token) {
    roles.reset()
    return
  }

  // Await the load whenever roles aren't loaded yet. loadEventUserRoles() dedupes
  // a concurrent in-flight request (e.g. one started by App.vue), so this resolves
  // once the roles are actually populated rather than skipping while loading.
  if (!roles.loaded) {
    await roles.loadEventUserRoles()
  }
}

export async function refreshAuthState() {
  const authState = await fetchAuthState()

  if (authState.session?.user && authState.jwt) {
    session.value = authState.session
    userStore().setLogin(authState.jwt, authState.session.user)
    await ensureRolesLoaded()
    return authState.jwt
  }

  resetLocalAuthState()
  return null
}

export async function initializeAuthState() {
  if (!initializationPromise) {
    initializationPromise = (async () => {
      try {
        const store = userStore()

        // Fast path: valid JWT in storage — skip getSession entirely. The 401
        // interceptor refreshes the JWT and slides the session on the next API
        // call that returns 401.
        if (store.token && !store.isTokenExpired) {
          return
        }

        // No bearer token either — user is logged out, don't bother the server.
        if (!getStoredBearerToken()) {
          return
        }

        await refreshAuthState()
      } finally {
        loaded.value = true
        initialized.value = true
      }
    })()
  }

  return initializationPromise
}

export async function loginWithPassword(email: string, password: string) {
  await signInWithEmail({ email, password })
  await refreshAuthState()
}

export async function registerWithPassword(input: {
  email: string
  password: string
  firstName: string
  lastName: string
}) {
  await signUpWithEmail(input)
  await refreshAuthState()
}

export async function logoutAuthState() {
  try {
    await signOutFromAuth()
  } finally {
    resetLocalAuthState()
  }
}

export const authSession = readonly(session)
export const authLoaded = readonly(loaded)
export const authInitialized = readonly(initialized)
